import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, STORE_SLUG } from "../lib/api";
import { fmtGameDate, fmtGameTime, sortGames } from "../lib/schedule";
import { MapPin, Home as HomeIcon, Plane } from "lucide-react";

export default function Schedule() {
  const [store, setStore] = useState(null);
  useEffect(() => { api.get(`/stores/${STORE_SLUG}`).then((r) => setStore(r.data)).catch(() => {}); }, []);

  const games = sortGames(store?.schedule || []);
  const now = Date.now();
  const nextIdx = games.findIndex((g) => new Date(g.datetime).getTime() >= now);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="schedule-page">
      <p className="text-xs uppercase tracking-[0.3em] text-[var(--gv-blue-bright)] font-bold mb-2">Gray Collegiate Academy • War Eagles</p>
      <h1 className="font-display text-5xl sm:text-6xl mb-6">2026 SCHEDULE</h1>
      {store?.schedule_image && (
        <a href={store.schedule_image} target="_blank" rel="noopener noreferrer" data-testid="schedule-image"
          className="block mb-8 border border-white/10 rounded-lg overflow-hidden hover:border-[var(--gv-blue)]/50 transition-colors">
          <img src={store.schedule_image} alt="2026 War Eagles Volleyball Schedule" className="w-full h-auto" />
        </a>
      )}
      <h2 className="text-base md:text-lg font-bold uppercase tracking-widest text-gray-300 mb-3">Game-by-Game</h2>
      <div className="space-y-2">
        {games.map((g, i) => {
          const isNext = i === nextIdx;
          const home = (g.location || "").toUpperCase() === "HOME";
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: Math.min(i * 0.03, 0.4) }}
              data-testid="schedule-game"
              className={`flex items-center gap-4 p-4 border ${isNext ? "border-[var(--gv-blue)] bg-[var(--gv-blue)]/10" : "border-white/10 bg-[#141518]"}`}>
              <div className="flex-shrink-0 text-center w-16">
                <p className="font-display text-2xl leading-none text-[var(--gv-blue-bright)]">{fmtGameDate(g.datetime).split(", ")[1] || fmtGameDate(g.datetime)}</p>
                <p className="text-[10px] uppercase tracking-widest text-gray-400">{fmtGameDate(g.datetime).split(", ")[0]}</p>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm sm:text-base truncate">{g.opponent}</p>
                <p className="text-xs text-gray-400 flex items-center gap-2">
                  {home ? <HomeIcon className="w-3 h-3" /> : <Plane className="w-3 h-3" />}
                  {home ? "Home" : "Away"} • {fmtGameTime(g.datetime)}
                  {isNext && <span className="ml-1 text-[var(--gv-blue-bright)] font-bold uppercase tracking-widest">• Next Up</span>}
                </p>
              </div>
              <div className="flex-shrink-0 text-right">
                {g.result ? <p className={`font-display text-xl ${g.result.toUpperCase().startsWith("W") ? "text-green-400" : "text-red-400"}`}>{g.result}</p> : <span className="text-xs text-gray-600 uppercase tracking-widest">—</span>}
                {g.score && <p className="text-xs text-gray-400">{g.score}</p>}
              </div>
            </motion.div>
          );
        })}
        {games.length === 0 && <p className="text-gray-400">Schedule coming soon.</p>}
      </div>
      {store?.address && (
        <p className="mt-10 text-xs text-gray-500 flex items-center gap-2"><MapPin className="w-4 h-4" /> {store.address}</p>
      )}
    </div>
  );
}
